import type { CommentAnchorType } from "../../../services/types";
import type { CommentType } from "../../comments";
import { buildCommentTree } from "../../comments/explorer/commentTreeModel";
import type { DiscussionModel } from "./types";

function sortAnchorsByCreatedAtThenId(
  a: CommentAnchorType,
  b: CommentAnchorType,
): number {
  const aTime = Date.parse(a.createdAt || "");
  const bTime = Date.parse(b.createdAt || "");

  if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) {
    return aTime - bTime;
  }

  return a.id - b.id;
}

export function buildDiscussionModel(
  comments: CommentType[] = [],
  anchors: CommentAnchorType[] = [],
): DiscussionModel {
  const safeComments = Array.isArray(comments) ? comments : [];
  const safeAnchors = Array.isArray(anchors) ? anchors : [];
  const tree = buildCommentTree(safeComments);

  const anchorsById = new Map<number, CommentAnchorType>();
  const anchorsByRootCommentId = new Map<number, CommentAnchorType>();

  safeAnchors
    .filter((anchor) => anchor && typeof anchor.id === "number")
    .sort(sortAnchorsByCreatedAtThenId)
    .forEach((anchor) => {
      anchorsById.set(anchor.id, anchor);
      if (typeof anchor.rootCommentId === "number") {
        anchorsByRootCommentId.set(anchor.rootCommentId, anchor);
      }
    });

  tree.all.forEach((comment) => {
    const anchoredThread = (comment as typeof comment & { anchoredThread?: CommentAnchorType | null }).anchoredThread;
    if (!anchoredThread?.id || anchorsByRootCommentId.has(comment.id)) return;

    const syntheticAnchor = {
      ...anchoredThread,
      gistId: comment.gistId ?? 0,
      editId: comment.editId ?? null,
      rootCommentId: comment.id,
      createdById: comment.userId,
      target: "BODY" as const,
      selectedHash: null,
      startOffset: 0,
      endOffset: 0,
      blockIndex: null,
      prefixText: null,
      suffixText: null,
      contextText: null,
      createdAt: comment.createdAt,
      updatedAt: comment.updatedAt || comment.createdAt,
      rootComment: comment,
      createdBy: comment.user as any,
    } as unknown as CommentAnchorType;

    anchorsById.set(syntheticAnchor.id, syntheticAnchor);
    anchorsByRootCommentId.set(comment.id, syntheticAnchor);
  });

  const anchoredRootCommentIds = new Set<number>(anchorsByRootCommentId.keys());
  const anchoredRootCount = tree.roots.filter((root) => anchoredRootCommentIds.has(root.id)).length;
  const generalRootCount = Math.max(0, tree.roots.length - anchoredRootCount);

  return {
    tree,
    roots: tree.roots,
    all: tree.all,
    byId: tree.byId,
    anchors: Array.from(anchorsById.values()).sort(sortAnchorsByCreatedAtThenId),
    anchorsById,
    anchorsByRootCommentId,
    anchoredRootCommentIds,
    generalRootCount,
    anchoredRootCount,
  };
}
